// Meta Pixel (Facebook Pixel) helper for client-side usage
// Pixel ID: 1280051553464647

declare global {
  interface Window {
    fbq?: any
    __fbqInited?: boolean
  }
}

const PIXEL_ID = '1280051553464647'
const SITE_ENV = process.env.NEXT_PUBLIC_SITE_ENV || 'development'
const IS_PROD = SITE_ENV === 'production' || SITE_ENV === 'staging'

function ensureFbq() {
  if (typeof window === 'undefined') return false
  // fbq is already loaded by the base script in layout
  return typeof window.fbq === 'function'
}

export function initMetaPixel() {
  if (typeof window === 'undefined') return
  if (!IS_PROD) return
  if (window.__fbqInited) return

  // fbq is initialized by the base script in layout.tsx
  // We just mark it as inited here
  window.__fbqInited = true
}

export function pageview() {
  if (typeof window === 'undefined') return
  if (!IS_PROD || !ensureFbq()) return
  // PageView is tracked by the base script
}

// Helper to normalize price to number
function toNum(v: any): number {
  const n = Number(v)
  return isFinite(n) ? n : 0
}

// Helper to get a safe ID string
function toId(...vals: any[]): string {
  for (const v of vals) {
    if (v) return String(v)
  }
  return 'unknown'
}

// Helper to get a safe name string
function toName(...vals: any[]): string {
  for (const v of vals) {
    if (v) return String(v)
  }
  return 'unknown'
}

// Track ViewContent event
export function viewContent(props: Record<string, any> = {}) {
  if (typeof window === 'undefined') return
  if (!IS_PROD || !ensureFbq()) return

  const contentName = toName(props.product_title, props.product_handle)
  const contentIds = [toId(props.product_handle, props.variant_id, props.product_id)]
  const value = toNum(props.price)
  const currency = props.currency || 'MXN'

  try {
    window.fbq('track', 'ViewContent', {
      content_name: contentName,
      content_ids: contentIds,
      content_type: 'product',
      value,
      currency,
    })
  } catch (e) {
    console.error('Meta Pixel ViewContent error:', e)
  }
}

// Track AddToCart event
export function addToCart(props: Record<string, any> = {}) {
  if (typeof window === 'undefined') return
  if (!IS_PROD || !ensureFbq()) return

  const contentName = toName(props.product_title, props.product_handle)
  const contentIds = [toId(props.product_handle, props.variant_id, props.product_id)]
  const value = toNum(props.price) * toNum(props.quantity || 1)
  const currency = props.currency || 'MXN'

  try {
    window.fbq('track', 'AddToCart', {
      content_name: contentName,
      content_ids: contentIds,
      content_type: 'product',
      value,
      currency,
    })
  } catch (e) {
    console.error('Meta Pixel AddToCart error:', e)
  }
}

// Track InitiateCheckout event
// props.event_id: shared dedup ID — the same ID must reach the CAPI
// InitiateCheckout (via /api/checkout/simple ?ic_eid=) or Meta counts the
// pixel and server events as two checkouts.
export function initiateCheckout(props: Record<string, any> = {}) {
  if (typeof window === 'undefined') return
  if (!IS_PROD || !ensureFbq()) return

  const items = Array.isArray(props.items)
    ? props.items.map((it: any) => ({
        id: toId(it.product_handle, it.variant_id, it.product_id),
        quantity: toNum(it.quantity) || 1,
        item_price: toNum(it.price),
      }))
    : []

  const value = toNum(props.cart_total) ||
    items.reduce((sum, it) => sum + (it.item_price * it.quantity), 0)

  try {
    const data = {
      content_category: 'checkout',
      content_ids: items.map((it: any) => it.id),
      contents: items,
      num_items: items.reduce((sum, it) => sum + it.quantity, 0),
      value,
      currency: props.currency || 'MXN',
    }
    if (props.event_id) {
      window.fbq('track', 'InitiateCheckout', data, { eventID: String(props.event_id) })
    } else {
      window.fbq('track', 'InitiateCheckout', data)
    }
  } catch (e) {
    console.error('Meta Pixel InitiateCheckout error:', e)
  }
}

// Track Purchase event (client-side, typically used with thank you page)
// Server-side CAPI is preferred for purchases - see meta-pixel-server.ts
export function purchase(props: Record<string, any> = {}) {
  if (typeof window === 'undefined') return
  if (!IS_PROD || !ensureFbq()) return

  const items = Array.isArray(props.items)
    ? props.items.map((it: any) => ({
        id: toId(it.product_handle, it.variant_id, it.product_id),
        quantity: toNum(it.quantity) || 1,
        item_price: toNum(it.price),
      }))
    : []

  const value = toNum(props.value)

  try {
    window.fbq('track', 'Purchase', {
      content_ids: items.map((it: any) => it.id),
      contents: items,
      content_type: 'product',
      num_items: items.reduce((sum, it) => sum + it.quantity, 0),
      value,
      currency: props.currency || 'MXN',
      transaction_id: props.transaction_id || undefined,
    })
  } catch (e) {
    console.error('Meta Pixel Purchase error:', e)
  }
}

// Track Lead event (used by perfil capilar quiz)
export function lead(props: {
  content_name: string
  currency?: string
  value?: number
  eventID?: string
}) {
  if (typeof window === 'undefined') return
  if (!IS_PROD || !ensureFbq()) return

  try {
    const data = {
      content_name: props.content_name,
      currency: props.currency || 'MXN',
      value: props.value ?? 0,
    }
    if (props.eventID) {
      window.fbq('track', 'Lead', data, { eventID: props.eventID })
    } else {
      window.fbq('track', 'Lead', data)
    }
  } catch (e) {
    console.error('Meta Pixel Lead error:', e)
  }
}

// Mapper for ecommerce events from our app (similar to ga.ts)
export function fireEcommerceFromApp(event: string, props: Record<string, any> = {}) {
  if (!IS_PROD) return

  switch (event) {
    case 'product_viewed': {
      viewContent({
        product_handle: props.product_handle,
        product_id: props.product_id,
        variant_id: props.variant_id,
        product_title: props.product_title,
        price: props.price,
        currency: 'MXN',
      })
      break
    }
    case 'add_to_cart': {
      addToCart({
        product_handle: props.product_handle,
        product_id: props.product_id,
        variant_id: props.variant_id,
        product_title: props.product_title,
        price: props.price,
        quantity: props.quantity,
        currency: 'MXN',
      })
      break
    }
    case 'checkout_initiated': {
      const items = Array.isArray(props.items)
        ? props.items.map((it: any) => ({
            product_handle: it.product_handle,
            product_id: it.product_id,
            variant_id: it.variant_id,
            price: it.price,
            quantity: it.quantity,
          }))
        : []

      initiateCheckout({
        items,
        cart_total: props.cart_total,
        currency: 'MXN',
        event_id: props.event_id,
      })
      break
    }
    case 'order_created': {
      // Note: Server-side CAPI is preferred for purchases
      // This client-side event is a fallback
      purchase({
        transaction_id: props.order_id,
        value: props.revenue,
        currency: props.currency || 'MXN',
        items: Array.isArray(props.items)
          ? props.items.map((it: any) => ({
              product_handle: it.product_handle || it.title?.toLowerCase().replace(/\s+/g, '-'),
              product_id: it.product_id,
              variant_id: it.variant_id,
              price: it.price,
              quantity: it.quantity,
            }))
          : [],
      })
      break
    }
    default:
      break
  }
}
